import { useState } from 'react';

const faqs = [
  {
    question: 'How quickly can Indoor-Restore Services respond to a water damage emergency?',
    answer: 'We offer 24/7 emergency response and typically arrive within 60 minutes of your call anywhere in Merced, CA and surrounding Merced County areas. Every minute counts with water damage — call (209) 451-2288 for immediate assistance.'
  },
  {
    question: 'Does Indoor-Restore Services work with insurance companies?',
    answer: 'Yes. We work directly with all major insurance companies to streamline the claims process. Our team thoroughly documents all damage and communicates with your adjuster so you receive the full coverage you deserve — at no extra stress to you.'
  },
  {
    question: 'What areas does Indoor-Restore Services serve?',
    answer: 'We serve Merced, Atwater, Livingston, Los Banos, Turlock, Modesto, Fresno, Madera, Chowchilla, Gustine, Newman, Patterson, Hilmar, Delhi, Winton, and all surrounding communities throughout Merced County and the Central Valley, CA.'
  },
  {
    question: 'How much does water damage restoration cost in Merced, CA?',
    answer: 'Costs vary depending on the extent of damage, affected area size, and services required. We offer free, no-obligation estimates. Call (209) 451-2288 or fill out our online form and a certified technician will assess your situation at no charge.'
  },
  {
    question: 'Is Indoor-Restore Services licensed and certified?',
    answer: 'Absolutely. We are fully licensed, bonded, and insured. Our technicians hold IICRC (Institute of Inspection, Cleaning and Restoration Certification) certifications and are continuously trained in the latest water damage restoration techniques and safety standards.'
  },
  {
    question: 'Can water damage cause mold in my Merced home?',
    answer: 'Yes — mold can begin growing within 24 to 48 hours of water damage, especially in Merced\'s warm Central Valley climate. That is why immediate professional water extraction and structural drying is critical. We provide complete mold remediation to ensure a safe, healthy indoor environment.'
  },
  {
    question: 'What should I do immediately after water damage?',
    answer: '1) Ensure safety — turn off electricity if water is near outlets. 2) Stop the water source if possible. 3) Call us at (209) 451-2288 for 24/7 emergency response. 4) Remove valuables from affected areas. 5) Do NOT use fans or HVAC — this can spread mold spores throughout your home.'
  },
  {
    question: 'Does Indoor-Restore Services handle commercial water damage?',
    answer: 'Yes. We provide comprehensive water damage restoration for both residential and commercial properties throughout Merced and the Central Valley. We understand the urgency of minimizing business downtime and work efficiently to restore your commercial property as fast as possible.'
  }
];

export default function FAQ() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const toggle = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section id="faq" className="py-20 bg-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-14">
          <span className="inline-block bg-teal-50 text-teal-700 text-xs font-semibold uppercase tracking-widest px-4 py-1.5 rounded-full mb-4">
            Common Questions
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Poppins, sans-serif' }}>
            Frequently Asked Questions
          </h2>
          <p className="text-lg text-gray-500 max-w-2xl mx-auto">
            Everything you need to know about water damage restoration in Merced, CA — answered by our certified experts.
          </p>
        </div>

        <div className="space-y-3">
          {faqs.map((faq, index) => (
            <article
              key={index}
              className={`border rounded-xl overflow-hidden transition-all duration-300 ${
                openIndex === index
                  ? 'border-teal-300 shadow-md'
                  : 'border-gray-200 hover:border-teal-200'
              }`}
            >
              <button
                onClick={() => toggle(index)}
                className="w-full flex items-center justify-between px-6 py-5 text-left cursor-pointer bg-white hover:bg-teal-50/40 transition-colors"
                aria-expanded={openIndex === index}
              >
                <h4 className="text-base font-semibold text-gray-900 pr-4 leading-snug">
                  <a id={`faq-${index}`} className="scroll-mt-24">{faq.question}</a>
                </h4>
                <div className={`w-8 h-8 flex items-center justify-center rounded-full flex-shrink-0 transition-all duration-300 ${
                  openIndex === index ? 'bg-teal-600 text-white rotate-180' : 'bg-gray-100 text-gray-500'
                }`}>
                  <i className="ri-arrow-down-s-line text-xl"></i>
                </div>
              </button>

              {/* Always rendered in DOM for SEO; visually shown/hidden for users */}
              <div
                className="transition-all duration-300 overflow-hidden"
                style={{
                  maxHeight: openIndex === index ? '400px' : '0px',
                }}
                aria-hidden={openIndex !== index}
              >
                <div className="px-6 pb-6 pt-1">
                  <p className="text-gray-600 text-sm leading-relaxed">{faq.answer}</p>
                </div>
              </div>

              {/* SEO-only: always in DOM, invisible to users, readable by crawlers */}
              <p
                className="sr-only"
                aria-hidden="true"
              >
                {faq.answer}
              </p>
            </article>
          ))}
        </div>

        <div className="mt-12 bg-teal-50 rounded-2xl p-8 text-center border border-teal-100">
          <i className="ri-question-answer-line text-4xl text-teal-600 mb-3 block"></i>
          <h3 className="text-xl font-bold text-gray-900 mb-2">Still have questions?</h3>
          <p className="text-gray-600 text-sm mb-6">
            Our certified water damage restoration experts in Merced are available 24/7 to answer any questions and provide a free estimate.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <a
              href="tel:2094512288"
              className="inline-flex items-center justify-center bg-teal-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer text-sm"
            >
              <i className="ri-phone-line mr-2"></i>
              Call (209) 451-2288
            </a>
            <button
              onClick={() => {
                const el = document.getElementById('contact');
                if (el) el.scrollIntoView({ behavior: 'smooth' });
              }}
              className="inline-flex items-center justify-center border border-teal-600 text-teal-600 px-6 py-3 rounded-lg font-semibold hover:bg-teal-50 transition-colors whitespace-nowrap cursor-pointer text-sm"
            >
              <i className="ri-mail-send-line mr-2"></i>
              Get Free Estimate
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
